import { Form, Button, Select, Typography } from "antd";
import FormItem from "antd/lib/form/FormItem";
import { useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router";
import {
  getAccessToken,
  getAssociatedSubscriptionsFromResponse,
  getSubscriptions,
  setAssociatedSubscriptions,
  setCurrentSubscriptionId
} from "services/auth";
import { AppLogoIcon } from "components/svg-icons";
import PiiConsent from "components/widgets/pii-consent";
import {
  getGenericPostLoginRedirectUrl,
  getProjectInviteRedirectUrlFromParams,
  resolveInvitePostLoginNavigationUrl,
  resolveSubscriptionForProjectInvite
} from "utils/project-invite-redirect";
import { sortSubscriptionsByName } from "utils/utils";

const { Title } = Typography;

function SubscriptionSelect() {
  const history = useHistory();
  const location = useLocation();
  const projectInviteRedirectUrl = useMemo(
    () => getProjectInviteRedirectUrlFromParams(location.search, location.hash),
    [location.search, location.hash]
  );
  const genericRedirectUrl = useMemo(
    () => getGenericPostLoginRedirectUrl(location.search),
    [location.search]
  );
  const postLoginRedirectUrl = projectInviteRedirectUrl || genericRedirectUrl;

  const [subscriptionForm] = Form.useForm();
  const [userSubscriptions, setUserSubscriptions] = useState<any[]>([]);

  useEffect(() => {
    const token = getAccessToken();
    if (!token) {
      history.push("/");
      return;
    }

    let cancelled = false;

    const loadSubscriptions = async () => {
      try {
        const subscriptionsRes: any = await getSubscriptions(token);
        if (cancelled) return;

        if (subscriptionsRes?.status !== 200) {
          history.push("/");
          return;
        }

        const subscriptions =
          getAssociatedSubscriptionsFromResponse(subscriptionsRes);
        setAssociatedSubscriptions(subscriptions);

        if (!Array.isArray(subscriptions) || subscriptions.length === 0) {
          history.push("/");
          return;
        }

        if (subscriptions.length === 1) {
          setCurrentSubscriptionId(subscriptions[0].id);
          if (postLoginRedirectUrl) {
            history.push(postLoginRedirectUrl);
          } else history.push("/projects");
          return;
        }

        const inviteSubscriptionId = resolveSubscriptionForProjectInvite(
          projectInviteRedirectUrl,
          subscriptions
        );
        if (inviteSubscriptionId) {
          setCurrentSubscriptionId(inviteSubscriptionId);
          history.push(
            resolveInvitePostLoginNavigationUrl(
              postLoginRedirectUrl,
              projectInviteRedirectUrl
            )
          );
          return;
        }

        const sortedSubscriptions = sortSubscriptionsByName(subscriptions);
        setUserSubscriptions(sortedSubscriptions);
        subscriptionForm.setFieldsValue({
          subscriptionId: sortedSubscriptions[0].id
        });
      } catch (error) {
        console.error("Error fetching subscriptions:", error);
        if (!cancelled) {
          history.push("/");
        }
      }
    };

    loadSubscriptions();

    return () => {
      cancelled = true;
    };
  }, [
    history,
    subscriptionForm,
    postLoginRedirectUrl,
    projectInviteRedirectUrl
  ]);

  const onSubscriptionFinish = (subscriptionData: any) => {
    setCurrentSubscriptionId(subscriptionData.subscriptionId);
    if (postLoginRedirectUrl) {
      history.push(postLoginRedirectUrl);
    } else history.push("/projects");
  };

  return (
    <div className="login">
      <div className="login-form">
        <div style={{ margin: "auto", display: "block" }}>
          <AppLogoIcon />
        </div>
        <div className="login-logo" style={{ paddingBottom: "50px" }}>
          <Title level={3} className="mt-3">
            Login
          </Title>
        </div>
        <div className="px-1">
          <Form
            onFinish={onSubscriptionFinish}
            form={subscriptionForm}
            labelCol={{ span: 50 }}
            wrapperCol={{ span: 30 }}
            scrollToFirstError
            layout="vertical"
            className="-mt-10"
          >
            <FormItem
              name="subscriptionId"
              label="Choose a subscription"
              layout="vertical"
            >
              <Select
                showSearch
                optionFilterProp="label"
                className="w-full"
                onChange={() => {}}
                options={userSubscriptions.map((sub: any) => {
                  return {
                    value: sub.id,
                    label: sub.name
                  };
                })}
              />
            </FormItem>

            <div
              style={{ paddingTop: "1em" }}
              className="w-full grid grid-cols-3 content-center"
            >
              <div className="flex items-center" />
              <div className="flex justify-center w-full pb-3">
                <Button htmlType="submit" size="middle" className="w-full">
                  Continue
                </Button>
              </div>
              <div />
            </div>
          </Form>
        </div>
      </div>
      <PiiConsent className="w-[350px] text-center text-xs text-ciq-gray-600 pt-7" />
    </div>
  );
}

export default SubscriptionSelect;
